// sessionRevocation.js
// -----------------------------------------------------------------------------
// Transport-agnostic session-revocation foundation for the VISINVOKE portal.
//
// Batch 1 wires ONLY the customer heartbeat (via startHeartbeat). The broadcast
// transport (BroadcastChannel + storage fallback), subscribe(), and the emit
// helpers are included now as the approved foundation for later batches; they are
// dormant until a caller uses them.
//
// Design invariants:
//  - Only an explicit 'revoked' classification (which callers return solely for
//    HTTP 401/403) logs a user out. Network errors / 5xx are 'transient' and only
//    trigger backoff — they never revoke.
//  - Event payloads carry NO secrets: only { type, reason, scope, senderId, at }.
//  - One transport per tab; subscribe()/unsubscribe() add/remove a handler from a
//    Set, so React StrictMode double-invocation and multiple providers are safe.
//  - Each startHeartbeat() call is an isolated controller with its own timers and
//    listeners, and returns a stop() that fully tears them down.
// -----------------------------------------------------------------------------

// Kill switch — flip to false to disable heartbeat + broadcast in one line.
export const REVOCATION_ENABLED = true;

// ---- Constants --------------------------------------------------------------
const CHANNEL_NAME            = 'visinvoke-auth';
const STORAGE_KEY             = 'visinvoke-auth-evt';
const BASE_MS                 = 90000; // visible-tab heartbeat base interval
const JITTER_MS               = 15000; // +/- jitter applied to the base interval
const EVENT_DEBOUNCE_MS       = 5000;  // coalesce focus/visibility/online bursts
const RECENT_CHECK_SUPPRESS_MS= 30000; // skip an interval tick if a check just ran
const BACKOFF_MS              = [90000, 180000, 300000]; // transient-error backoff

const SUPPORTS_BC =
  typeof window !== 'undefined' && typeof window.BroadcastChannel !== 'undefined';

// Per-tab sender id — used to suppress a tab reacting to its own broadcast.
const TAB_ID =
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 't-' + Math.random().toString(36).slice(2) + '-' + Date.now().toString(36);

// ---- Broadcast transport (one per tab, ref-free Set of handlers) ------------
let channel = null;          // BroadcastChannel instance (lazy)
let storageBound = false;    // whether the 'storage' fallback listener is attached
const handlers = new Set();  // active subscription handlers

function dispatch(msg) {
  if (!msg || typeof msg !== 'object') return;
  if (msg.senderId === TAB_ID) return; // ignore our own events
  handlers.forEach((h) => { try { h(msg); } catch (_e) { /* isolate handlers */ } });
}

function onStorage(ev) {
  if (ev.key !== STORAGE_KEY || !ev.newValue) return;
  try { dispatch(JSON.parse(ev.newValue)); } catch (_e) { /* ignore malformed */ }
}

// Idempotent: creates the single transport for this tab if not already present.
function ensureTransport() {
  if (typeof window === 'undefined') return;
  if (SUPPORTS_BC && !channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (ev) => dispatch(ev.data);
    } catch (_e) {
      channel = null;
    }
  }
  if (!channel && !storageBound) {
    window.addEventListener('storage', onStorage);
    storageBound = true;
  }
}

/**
 * Subscribe to session-revocation broadcasts from other same-origin tabs.
 * Returns an unsubscribe function. Self-emitted events are never delivered.
 */
export function subscribe(handler) {
  if (typeof handler !== 'function') return () => {};
  ensureTransport();
  handlers.add(handler);
  return function unsubscribe() { handlers.delete(handler); };
}

function storagePost(payload) {
  try {
    if (typeof localStorage !== 'undefined') {
      // write-then-remove so other tabs get a 'storage' event; value is transient.
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (_e) { /* storage unavailable — best effort */ }
}

function post(type, reason, scope) {
  // Payload deliberately contains no tokens, cookies, passwords or PII.
  const payload = {
    type,
    reason: reason || null,
    scope: scope || 'all',
    senderId: TAB_ID,
    at: Date.now(),
  };
  ensureTransport();
  if (channel) {
    try { channel.postMessage(payload); return; } catch (_e) { /* fall through */ }
  }
  storagePost(payload);
}

/** Emit a hard-revocation event (used by later batches: logout / reset). */
export function broadcastRevoked({ reason, scope } = {}) {
  if (!REVOCATION_ENABLED) return;
  post('session-revoked', reason, scope);
}

/** Emit a re-check request (used by a later batch: password-change). */
export function broadcastRevalidate({ reason, scope } = {}) {
  if (!REVOCATION_ENABLED) return;
  post('session-revalidate', reason, scope);
}

// ---- Heartbeat engine -------------------------------------------------------
/**
 * Start an authenticated-session heartbeat.
 *
 * @param {object}   opts
 * @param {string}   opts.scope      - informational label ('customer' | 'staff').
 * @param {function} opts.check      - async () => 'ok' | 'revoked' | 'transient'.
 *                                     MUST return 'revoked' only for HTTP 401/403.
 * @param {function} opts.onRevoked  - called once when check() returns 'revoked'.
 * @returns {function} stop() - tears down timers and listeners.
 */
export function startHeartbeat({ scope = 'default', check, onRevoked } = {}) {
  if (
    !REVOCATION_ENABLED ||
    typeof check !== 'function' ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return () => {};
  }

  let stopped = false;
  let timerId = null;
  let inFlight = false;      // single-flight guard
  let lastCheckAt = 0;       // last time any check actually started
  let lastEventCheckAt = 0;  // last time an event-driven check started (debounce)
  let backoffIndex = -1;     // -1 = normal cadence; >= 0 = transient backoff step

  const clearTimer = () => {
    if (timerId !== null) { clearTimeout(timerId); timerId = null; }
  };

  const nextDelay = () => {
    if (backoffIndex >= 0) {
      return BACKOFF_MS[Math.min(backoffIndex, BACKOFF_MS.length - 1)];
    }
    const jitter = (Math.random() * 2 - 1) * JITTER_MS; // +/- JITTER_MS
    return Math.max(1000, BASE_MS + jitter);
  };

  const schedule = () => {
    clearTimer();
    if (stopped || document.hidden) return; // paused while hidden
    timerId = setTimeout(onTick, nextDelay());
  };

  async function runCheck(viaEvent) {
    if (stopped) return;
    const now = Date.now();
    if (viaEvent) {
      if (now - lastEventCheckAt < EVENT_DEBOUNCE_MS) return; // debounce bursts
      lastEventCheckAt = now;
    } else if (now - lastCheckAt < RECENT_CHECK_SUPPRESS_MS) {
      return; // interval tick suppressed: an event-driven check ran recently
    }
    if (inFlight) return; // single-flight
    inFlight = true;
    lastCheckAt = now;

    let result;
    try { result = await check(); }
    catch (_e) { result = 'transient'; } // unknown failure never logs out
    inFlight = false;
    if (stopped) return;

    if (result === 'revoked') {
      stopped = true;
      clearTimer();
      if (onRevoked) { try { onRevoked({ scope, reason: 'heartbeat' }); } catch (_e) {} }
      return;
    }
    if (result === 'transient') {
      backoffIndex = backoffIndex < 0 ? 0 : Math.min(backoffIndex + 1, BACKOFF_MS.length - 1);
    } else {
      backoffIndex = -1; // 'ok' clears backoff
    }
  }

  async function onTick() {
    timerId = null;
    if (stopped) return;
    if (!document.hidden) await runCheck(false);
    schedule();
  }

  const onFocus = () => { runCheck(true); };
  const onOnline = () => { runCheck(true); };
  const onVisibility = () => {
    if (document.hidden) {
      clearTimer(); // pause while hidden
    } else {
      runCheck(true); // immediate check on becoming visible
      schedule();     // resume cadence
    }
  };

  window.addEventListener('focus', onFocus);
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisibility);
  schedule(); // begins only if currently visible

  return function stop() {
    stopped = true;
    clearTimer();
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
